package com.appsyde.shared.modules.auth.data

import com.appsyde.shared.getAccessToken
import com.appsyde.shared.modules.auth.domain.model.DomainResult
import com.appsyde.shared.modules.auth.domain.repo.AuthRepository
import com.appsyde.shared.network.SessionResponse
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.accept
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.parameter
import io.ktor.http.ContentType

class AuthRepositoryImpl(
    private val client: HttpClient,
    private val serverBaseUrl: String
) : AuthRepository {

    private suspend inline fun <T> mapToDomainResult(block: suspend () -> T): DomainResult<T> {
        return try {
            DomainResult.Success(block())
        } catch (e: Exception) {
            DomainResult.Failure(mapExceptionToDomainError(e))
        }
    }

    override suspend fun getLoginUrl(forceConsent: Boolean): DomainResult<String> = mapToDomainResult {
        val url = "$serverBaseUrl/auth/login"
        val response = client.get(url) {
            accept(ContentType.Application.Json)
            parameter("prompt", if (forceConsent) "consent" else null)
        }.body<AuthLoginResponse>()
        response.authUrl
    }

    override suspend fun getLoginUrlIdentity(redirectUri: String): DomainResult<String> = mapToDomainResult {
        val url = "$serverBaseUrl/auth/login/start"
        val response = client.get(url) {
            accept(ContentType.Application.Json)
            parameter("redirect_uri", redirectUri)
        }.body<AuthLoginResponse>()
        response.authUrl
    }

    override suspend fun getSignupUrl(redirectUri: String): DomainResult<String> = mapToDomainResult {
        val url = "$serverBaseUrl/auth/start/signup"
        val response = client.get(url) {
            accept(ContentType.Application.Json)
            parameter("redirect_uri", redirectUri)
        }.body<AuthLoginResponse>()
        response.authUrl
    }

    override suspend fun getLoginUrlLanding(redirectUri: String): DomainResult<String> = mapToDomainResult {
        val url = "$serverBaseUrl/auth/start/login"
        val response = client.get(url) {
            accept(ContentType.Application.Json)
            parameter("redirect_uri", redirectUri)
        }.body<AuthLoginResponse>()
        response.authUrl
    }

    override suspend fun exchangeCode(code: String): DomainResult<Unit> = mapToDomainResult {
        val url = "$serverBaseUrl/auth/exchange?code=$code"
        client.get(url) {
            accept(ContentType.Application.Json)
        }
    }

    override suspend fun checkSession(): DomainResult<Boolean> = mapToDomainResult {
        val token = getAccessToken()
        val response = client.get("$serverBaseUrl/auth/status") {
            accept(ContentType.Application.Json)
            if (!token.isNullOrBlank()) header("Authorization", "Bearer $token")
        }.body<SessionResponse>()
        response.connected
    }

    override suspend fun getSessionToken(): DomainResult<String?> = mapToDomainResult {
        val response = client.get("$serverBaseUrl/auth/session-token") {
            accept(ContentType.Application.Json)
        }.body<SessionTokenResponse>()
        response.accessToken
    }

    override suspend fun logout(): DomainResult<Boolean> = mapToDomainResult {
        client.post("$serverBaseUrl/auth/logout") {
            accept(ContentType.Application.Json)
        }
        true
    }
}
